/* eslint-disable import/prefer-default-export */
import { store } from '@redux/store'
import { setStoreCartCreationData, clearCart } from '@redux/modules/cart'
import { setOrder } from '@redux/modules/checkout'
import { getOrder, setOrderField } from '@helpers/checkout/global'
import { setEquickDecision } from '@helpers/checkout/payment-section/rtg-finance'
import { setContactInfo, setShippingAddressInfo } from '@helpers/checkout/shipping-section'
import { geoLocation, getCurrentLocation } from '@helpers/geo-location'
import {
  addStoreCartEvent,
  removeStoreCartEvent,
  analyticsProduct,
  addToDataLayer_nextgen,
} from '@helpers/google-tag-manager'
import { getFromBrowserStorage, saveLocalStorage } from '@helpers/storage'
import { getStoreCart, updateLineItems } from '@services/checkout'
import { getCart } from '@services/cart'

/**
 * Checks if fetched store cart is valid. If not, returns false. If it is, then:
 * - updates the contact, financePlan, and shippingAddress fields on the Order object in redux and sessionStorage for CartV1,
 * - adds the storeInfo property on the Order object in redux and sessionStorage with data from the store cart,
 * - sets storeCartCreationData value in redux so the store cart will be imported by Cart,
 * - updates the location cookie to match the store cart shipping address zip code,
 * - pushes GA event for loading the store cart, then
 * - returns true
 * @param {object} fetchedStoreCart
 * @param {boolean} didMoveCartItemsToFavorites if true, will cause StoreCartModal to display on Cart page
 * @param {object} apolloClient
 * @param {boolean} [isCartV1=true] Optional. If true (default), updates contact and shipping info for CartV1.
 * @param {boolean} [isImported=false] Optional. If true, indicates the store cart has already been imported.
 * @returns {boolean}
 */
export const insertStoreCartDetails = (
  fetchedStoreCart,
  didMoveCartItemsToFavorites = false,
  apolloClient,
  isCartV1 = true,
  isImported = false,
) => {
  const { storeCartArray } = fetchedStoreCart

  // If fetchedStoreCart includes property storeCartArray, then take the latest cart in the array, else use fetchedStoreCart
  const storeCart =
    storeCartArray && storeCartArray.length ? storeCartArray[storeCartArray.length - 1] : { ...fetchedStoreCart }

  const isValidStoreCart = Array.isArray(storeCart?.lineItems) && storeCart?.lineItems.length > 0

  if (isValidStoreCart) {
    const {
      contact,
      lineItems,
      quickScreenInfo,
      salesPersonEmail,
      salesPersonInfo,
      shippingAddress,
      storeAddress,
      storeCartId,
      storeName,
      storeNumber,
      storePhoneNumber,
    } = storeCart

    const storeInfo = {
      salesPersonEmail,
      salesPersonInfo,
      storeAddress,
      storeCartId,
      storeName,
      storeNumber,
      storePhoneNumber,
    }
    setOrderField('storeInfo', storeInfo)

    if (isCartV1 && contact && shippingAddress) {
      setContactInfo(contact)
      setShippingAddressInfo(shippingAddress)
    }

    if (isCartV1 && quickScreenInfo) {
      const order = getOrder()
      const { financePlan = {} } = order
      const { creditLimit, decision, offerExpiryDate, synchronyEapplyUrl } = quickScreenInfo
      const isPrequalified =
        ['APPROVED', 'CREDIT_APPROVED'].includes(decision) && !!creditLimit && !!offerExpiryDate && !!synchronyEapplyUrl

      setOrderField('financePlan', { ...financePlan, isPrequalified, quickScreenInfo })
    }

    if (apolloClient && shippingAddress?.zip) {
      geoLocation(apolloClient, shippingAddress.zip, false)
    }

    store.dispatch(
      setStoreCartCreationData({
        associateName: salesPersonInfo,
        didMoveCartItemsToFavorites,
        email: contact?.email ?? '',
        id: storeCartId,
        isImported,
        itemCount: lineItems.length,
      }),
    )
    addStoreCartEvent(storeCart)
  }

  return isValidStoreCart
}

/**
 * ONLY USED FOR CART V1 - FOR CART V2, USE THE reloadStoreCartMutation FROM useCartV2 HOOK
 * Uses the storeCart.contact.email and storeInfo.storeCartId fields from the order object in sessionStorage to call the
 * storeCartByEmail API endpoint to reload a previously imported store cart or to confirm status of Synchrony pre-approval
 * offer. If the checksum value in the response has changed, then loads the updated store cart.
 * @param {boolean} [isUpdatingSynchronyDecision] optional boolean - set to true to check Sychrony decision, false or omit to reload storecart
 * @returns if isUpdatingSynchronyDecision then returns null and updates order.financePlan, else returns updated storeCart object
 */
export const reloadStoreCart = async (isUpdatingSynchronyDecision = false) => {
  const { storeCart = {}, storeInfo = {} } = getFromBrowserStorage('session', 'order') ?? {}

  const checksum = storeCart?.checksum ?? ''
  const email = storeCart?.contact?.email ?? ''
  const storeCartId = storeInfo?.storeCartId ?? ''

  if (storeCartId && email) {
    const fetchedStoreCart = await getStoreCart({ email, storeCartId })

    if (isUpdatingSynchronyDecision) {
      const { quickScreenInfo = {} } = fetchedStoreCart ?? {}
      setEquickDecision(quickScreenInfo?.decision)
      return null
    }

    if (checksum !== fetchedStoreCart?.checksum) {
      // only refresh store cart if the checksum has changed, which indicates the cart was updated by the sales person
      insertStoreCartDetails(fetchedStoreCart)
    }
  }
  return null
}

// remove item from the store cart
export const removeStoreCartItem = async (lineItems, orderId, setSkusNotAvailable, storeCartItemToRemove) => {
  const { isUnavailable, sku: skuToRemove, unitPrice, quantity } = storeCartItemToRemove
  let newLineItems = [...lineItems]

  // if quantity > 1 and product is available, reduce quantity by 1, otherwise, remove the item from newLineItems
  if (quantity > 1 && !isUnavailable) {
    const itemToRemove = newLineItems.find(i =>
      i.sku === '83333333' ? i.sku === skuToRemove && i.unitPrice === unitPrice : i.sku === skuToRemove,
    )
    itemToRemove.quantity -= 1
  } else {
    newLineItems = newLineItems.filter(i =>
      i.sku === '83333333' ? i.sku !== skuToRemove && i.unitPrice !== unitPrice : i.sku !== skuToRemove,
    )
  }

  try {
    const location = getCurrentLocation()
    const newOrder = await updateLineItems({
      orderId,
      lineItems: newLineItems,
      region: location.region,
      zone: parseInt(location.price_zone),
      distribution_index: parseInt(location.distribution_index),
    })
    if (newOrder && newOrder.storeCart) {
      store.dispatch(setOrder(newOrder))
      removeStoreCartEvent(newOrder.storeCart, storeCartItemToRemove)
      if (isUnavailable) {
        setSkusNotAvailable(current => current.filter(i => i.sku !== skuToRemove))
      }
    }
  } catch (error) {
    console.error('Could not remove the Store Cart item: ', error)
  }
}

export const toggleStoreCartItemESC = async (lineItems, orderId, sku) => {
  const newLineItems = lineItems.map(itm => {
    if (itm.sku === sku) {
      return { ...itm, warrantyEnabled: !itm.warrantyEnabled }
    }
    return itm
  })

  try {
    const location = getCurrentLocation()
    const newOrder = await updateLineItems({
      orderId,
      lineItems: newLineItems,
      region: location.region,
      zone: parseInt(location.price_zone),
      distribution_index: parseInt(location.distribution_index),
    })

    if (newOrder) {
      store.dispatch(setOrder(newOrder))
    }
  } catch (error) {
    console.error('Error removing warranty from store cart item: ', error)
  }
}

export const importStoreCartV1 = (addFavorite, apolloClient, storeCart, user) => {
  const { cartItems = [] } = getFromBrowserStorage('local', 'cart') ?? { cartItems: [] }
  const cartItemsToFavorite = cartItems
    ?.map(item => item?.product ?? {})
    ?.filter(item => !item?.isConfigurable && item?.sku !== '83333333') // Gift Cards and Modular Configured items cannot be Favorited
  const hasItemsToFavorite = cartItemsToFavorite?.length > 0

  const isValid = insertStoreCartDetails(storeCart, hasItemsToFavorite, apolloClient)

  if (isValid) {
    // If there are existing Online Cart Items that can be Favorited, move them to Favorites before navigating to Cart
    if (hasItemsToFavorite) {
      cartItemsToFavorite?.forEach((item, index) => {
        const trackingData = {
          ecommerce: {
            add_to_wishlist: {
              position: index,
              list: 'search',
              products: [analyticsProduct(item)],
              auth_user_id: user?.uid || null,
            },
          },
        }
        addToDataLayer_nextgen('ee_add_to_wishlist', trackingData)
        addFavorite(user)(item?.sku)
      })
    }

    // Clear the V1 cart if there are any cart items
    if (cartItems?.length > 0) {
      store.dispatch(clearCart())
    }

    // Save store cart to local storage to persist across tabs
    saveLocalStorage('cart', { cartItems: [], storeCart })

    return { success: true }
  }

  return { success: false, error: 'There was an issue with your Store Cart' }
}

export const importStoreCartV2 = async (
  addFavorite,
  apolloClient,
  cartV2,
  storeCart,
  user,
  importStoreCartMutation = null,
) => {
  // If cartV2 is undefined, try to fetch it using id from localStorage
  let currentCart = cartV2
  if (!currentCart) {
    const cartInfo = getFromBrowserStorage('local', 'cartInfo')
    if (cartInfo?.id) {
      try {
        currentCart = await getCart(cartInfo.id)
      } catch (error) {
        console.warn('Could not fetch existing cart:', error)
        currentCart = { lineItems: [] }
      }
    } else {
      currentCart = { lineItems: [] }
    }
  }

  const { lineItems = [] } = currentCart ?? { lineItems: [] }
  const cartItemsToFavorite = lineItems
    ?.filter(item => !item?.isConfigurable && !item?.isStoreSku && item?.sku !== '83333333') // Gift Cards, Store Cart Items and Configured items cannot be Favorited
    ?.map(item => ({
      ...(item?.productDetails ?? {}),
      quantity: item?.quantity,
      sku: item?.sku,
      unitPrice: item?.unitPrice,
    }))
  const hasItemsToFavorite = cartItemsToFavorite?.length > 0

  const isValid = insertStoreCartDetails(storeCart, hasItemsToFavorite, apolloClient, false, false)

  if (isValid) {
    // If there are existing Cart V2 Items that can be Favorited, move them to Favorites before navigating to Cart
    if (hasItemsToFavorite) {
      cartItemsToFavorite?.forEach((item, index) => {
        const trackingData = {
          ecommerce: {
            add_to_wishlist: {
              position: index,
              list: 'search',
              products: [analyticsProduct(item)],
              auth_user_id: user?.uid || null,
            },
          },
        }
        addToDataLayer_nextgen('ee_add_to_wishlist', trackingData)
        addFavorite(user)(item?.sku)
      })
    }

    // If importStoreCartMutation is provided, perform the actual cart import immediately
    if (importStoreCartMutation) {
      const { contact, storeCartId } = storeCart
      return importStoreCartMutation
        .mutateAsync({ email: contact?.email ?? '', storeCartId })
        .then(() => {
          // Update storeCartCreationData to mark as imported
          store.dispatch(
            setStoreCartCreationData({
              associateName: storeCart.salesPersonInfo,
              didMoveCartItemsToFavorites: hasItemsToFavorite,
              email: contact?.email ?? '',
              id: storeCartId,
              isImported: true,
              itemCount: storeCart.lineItems.length,
            }),
          )
          return { success: true }
        })
        .catch(() => ({ success: false, error: 'There was an issue importing your Store Cart' }))
    }

    // Note: not necessary to clear the V2 cart since we will create a new cart for the store cart import
    // This also allows for the possibility of performing a quick undo to restore the previous version of the cart
    return { success: true }
  }

  return { success: false, error: 'There was an issue with your Store Cart' }
}
